import { ApplicationsDocument } from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";

const APPLICATION_LIMIT = 20;

/**
 * Fetches and paginates applications with search functionality.
 *
 * Queries the GraphQL API for applications with support for load-more pagination
 * and debounced text search by application name.
 *
 * @param searchText - Search query to filter applications by name (case-insensitive)
 * @returns An object containing:
 *   - applications: Array of Application objects for the current page(s)
 *   - loading: Boolean indicating if the initial query is in progress
 *   - loadMore: Function to fetch the next page of applications
 *   - hasMore: Boolean indicating if more applications are available to load
 */
export function useApplications(searchText: string) {
  const { data, loading, fetchMore } = useQuery(ApplicationsDocument, {
    variables: {
      limit: APPLICATION_LIMIT,
      offset: 0,
      searchText: searchText || null,
    },
  });

  const items = data?.applications?.items ?? [];
  const hasMore = data?.applications?.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: {
        limit: APPLICATION_LIMIT,
        offset: items.length,
        searchText: searchText || null,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          applications: {
            ...fetchMoreResult.applications,
            items: [...prev.applications.items, ...fetchMoreResult.applications.items],
          },
        };
      },
    });
  };

  return { applications: items, loading, loadMore, hasMore };
}
